import React, { useState, useEffect, useCallback } from 'react';
import { Container, Button, Table, Modal, Form, Spinner, Alert, Row, Col, Badge, Card } from 'react-bootstrap';
import apiClient, { API_ENDPOINTS } from '../utils/api';
import { formatCurrency, formatDate } from '../utils/helpers';

const thisYear = new Date().getFullYear();
const thisMonth = `${thisYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

// 備品費の勘定科目(決算集計用)。バックエンド既定は 消耗品費。
// 「外注費」を選ぶと決算集計で予約清掃費と同じ科目に合算される(追加清掃など)
export const EXPENSE_CATEGORIES = ['消耗品費', '修繕費', '水道光熱費', '通信費', '外注費', '支払手数料', '雑費'];

const initialExpenseForm = { date: '', label: '', amount: '', category: '消耗品費', split_exempt: false, notes: '' };

function Ledger() {
  const [year, setYear] = useState(thisYear);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // 月詳細
  const [selectedMonth, setSelectedMonth] = useState(thisMonth);
  const [monthDetail, setMonthDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 備品費モーダル
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [expenseForm, setExpenseForm] = useState(initialExpenseForm);
  const [saving, setSaving] = useState(false);

  // 精算設定モーダル
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configForm, setConfigForm] = useState(null);

  // 月別固定費モーダル(基本設定はそのまま、この月だけ上書き)
  const [showFixedModal, setShowFixedModal] = useState(false);
  const [fixedForm, setFixedForm] = useState(null); // { month, costs: [{label, amount, category}], is_override }

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await apiClient.get(API_ENDPOINTS.LEDGER_SUMMARY, { params: { year } });
      setSummary(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || '精算データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [year]);

  const fetchMonthDetail = useCallback(async (month) => {
    if (!month) {
      // 月入力をクリアしたら前月の詳細を残さない(残すとCSVボタンが空の月でAPIを叩く)
      setMonthDetail(null);
      return;
    }
    try {
      setDetailLoading(true);
      const res = await apiClient.get(`${API_ENDPOINTS.LEDGER_SUMMARY}/${month}`);
      setMonthDetail(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || '月次詳細の取得に失敗しました');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchMonthDetail(selectedMonth); }, [selectedMonth, fetchMonthDetail]);

  const refreshAll = async () => {
    await Promise.all([fetchSummary(), fetchMonthDetail(selectedMonth)]);
  };

  // --- 備品費 ---
  const openCreateExpense = () => {
    setEditingExpenseId(null);
    setExpenseForm({
      ...initialExpenseForm,
      date: selectedMonth ? `${selectedMonth}-01` : new Date().toISOString().slice(0, 10),
    });
    setShowExpenseModal(true);
  };

  const openEditExpense = (expense) => {
    setEditingExpenseId(expense.id);
    setExpenseForm({
      date: (expense.date || '').slice(0, 10),
      label: expense.label || '',
      amount: String(expense.amount ?? ''),
      category: expense.category || '消耗品費',
      split_exempt: !!expense.split_exempt,
      notes: expense.notes || '',
    });
    setShowExpenseModal(true);
  };

  const handleSaveExpense = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        date: expenseForm.date,
        label: expenseForm.label,
        amount: Number(expenseForm.amount || 0),
        category: expenseForm.category,
        split_exempt: expenseForm.split_exempt,
        notes: expenseForm.notes, // 空文字は「備考をクリア」として送る(null だとバックエンドが未指定扱いでスキップ)
      };
      if (editingExpenseId) {
        await apiClient.put(`${API_ENDPOINTS.LEDGER_EXPENSES}/${editingExpenseId}`, payload);
        setMessage('備品費を更新しました');
      } else {
        await apiClient.post(API_ENDPOINTS.LEDGER_EXPENSES, payload);
        setMessage('備品費を登録しました');
      }
      setShowExpenseModal(false);
      await refreshAll();
    } catch (err) {
      setError(err.response?.data?.detail || '備品費の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('この備品費を削除しますか？')) return;
    setError('');
    try {
      await apiClient.delete(`${API_ENDPOINTS.LEDGER_EXPENSES}/${expenseId}`);
      setMessage('備品費を削除しました');
      await refreshAll();
    } catch (err) {
      setError(err.response?.data?.detail || '備品費の削除に失敗しました');
    }
  };

  // --- 支払記録 ---
  const handleTogglePaid = async (month, currentPayment, ownerShare) => {
    const nowPaid = currentPayment?.status === 'paid';
    setError('');
    try {
      await apiClient.put(`${API_ENDPOINTS.LEDGER_PAYMENTS}/${month}`, nowPaid
        ? { status: 'unpaid' }
        : {
          status: 'paid',
          paid_date: new Date().toISOString().slice(0, 10),
          paid_amount: ownerShare,
        });
      setMessage(nowPaid ? `${month} を未払いに戻しました` : `${month} をオーナー支払済みにしました`);
      await refreshAll();
    } catch (err) {
      setError(err.response?.data?.detail || '支払記録の更新に失敗しました');
    }
  };

  // --- 月別固定費の上書き ---
  const openFixedModal = async () => {
    if (!selectedMonth) return;
    setError('');
    try {
      const res = await apiClient.get(`${API_ENDPOINTS.LEDGER_FIXED_COSTS}/${selectedMonth}`);
      setFixedForm({
        month: selectedMonth,
        is_override: res.data.is_override,
        costs: res.data.costs.map((c) => ({
          label: c.label,
          amount: String(c.amount),
          category: c.category || '通信費',
          split_exempt: !!c.split_exempt,
        })),
      });
      setShowFixedModal(true);
    } catch (err) {
      setError(err.response?.data?.detail || '固定費の取得に失敗しました');
    }
  };

  const handleSaveFixed = async () => {
    setSaving(true);
    setError('');
    try {
      const costs = fixedForm.costs
        .filter((c) => c.label.trim())
        .map((c) => ({
          label: c.label,
          amount: Number(c.amount || 0),
          category: c.category || '通信費',
          split_exempt: !!c.split_exempt,
        }));
      await apiClient.put(`${API_ENDPOINTS.LEDGER_FIXED_COSTS}/${fixedForm.month}`, { costs });
      setMessage(`${fixedForm.month} の固定費を上書きしました（基本設定は変わりません）`);
      setShowFixedModal(false);
      await refreshAll();
    } catch (err) {
      setError(err.response?.data?.detail || '固定費の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleResetFixed = async () => {
    setSaving(true);
    setError('');
    try {
      await apiClient.delete(`${API_ENDPOINTS.LEDGER_FIXED_COSTS}/${fixedForm.month}`);
      setMessage(`${fixedForm.month} の固定費を基本設定に戻しました`);
      setShowFixedModal(false);
      await refreshAll();
    } catch (err) {
      setError(err.response?.data?.detail || '固定費のリセットに失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // --- 精算設定 ---
  const openConfigModal = () => {
    const config = summary?.config;
    if (!config) return;
    setConfigForm({
      cleaning_fee: String(config.cleaning_fee),
      owner_split_percent: String(config.owner_split_percent),
      monthly_costs: config.monthly_costs.map((c) => ({
        label: c.label,
        amount: String(c.amount),
        category: c.category || '通信費',
        split_exempt: !!c.split_exempt,
      })),
      ota_rates: Object.entries(config.ota_rates).map(([source, rate]) => ({ source, rate: String(rate) })),
    });
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        cleaning_fee: Number(configForm.cleaning_fee || 0),
        owner_split_percent: Number(configForm.owner_split_percent || 0),
        monthly_costs: configForm.monthly_costs
          .filter((c) => c.label.trim())
          .map((c) => ({
            label: c.label,
            amount: Number(c.amount || 0),
            category: c.category || '通信費',
            split_exempt: !!c.split_exempt,
          })),
        ota_rates: Object.fromEntries(
          configForm.ota_rates
            .filter((r) => r.source.trim())
            .map((r) => [r.source, Number(r.rate || 0)])
        ),
      };
      await apiClient.put(API_ENDPOINTS.LEDGER_CONFIG, payload);
      setMessage('精算設定を保存しました');
      setShowConfigModal(false);
      await refreshAll();
    } catch (err) {
      setError(err.response?.data?.detail || '精算設定の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // --- 支払明細書・CSVのダウンロード ---
  const downloadFile = async (url, filename, doneMessage) => {
    setError('');
    try {
      const res = await apiClient.get(url, { responseType: 'blob' });
      const objectUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
      setMessage(doneMessage);
    } catch (err) {
      setError('ファイルの出力に失敗しました');
    }
  };

  // オーナーへの支払い用の整形済み明細書(そのまま渡せる)
  const handleExportStatement = (month, fmt) => downloadFile(
    `${API_ENDPOINTS.LEDGER_STATEMENT}/${month}?fmt=${fmt}`,
    `お支払明細書_${month}.${fmt}`,
    `${month} のお支払明細書（${fmt === 'pdf' ? 'PDF' : 'Excel'}）を出力しました`,
  );

  const handleExportYearCsv = () => downloadFile(
    `${API_ENDPOINTS.LEDGER_EXPORT_YEAR}/${year}`,
    `オーナー精算書_${year}年間.csv`,
    `${year}年の年間精算書CSVを出力しました`,
  );

  if (loading && !summary) {
    return (
      <Container className="d-flex justify-content-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  const months = summary?.months || [];
  const totals = summary?.totals;

  return (
    <Container fluid>
      <div className="page-header">
        <h1 className="page-title">オーナー精算</h1>
        <Button variant="outline-secondary" onClick={openConfigModal}>精算設定（手数料率・清掃費・固定費）</Button>
      </div>

      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <p className="text-muted small mb-3">
        台帳（予約管理）のチェックイン月で集計します。
        利益 ＝ 売上 − サイト手数料 − 清掃費（{formatCurrency(summary?.config?.cleaning_fee)} / 予約） − 備品費 − 月額固定費。
        清掃はオーナー様が担うため、<strong>お支払額 ＝ 清掃費 ＋ 利益の {summary?.config?.owner_split_percent}%</strong>（キャンセル予約は除外）。
        支払明細書は Excel / PDF でそのまま渡せる形で出力できます。
      </p>

      {/* 年間サマリー */}
      <div className="toolbar-row mb-2 d-flex flex-wrap gap-2 align-items-center">
        <Form.Select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ maxWidth: '140px' }}
        >
          {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map((y) => (
            <option key={y} value={y}>{y}年</option>
          ))}
        </Form.Select>
        <Button variant="outline-primary" size="sm" onClick={handleExportYearCsv}>
          年間精算書CSV
        </Button>
        {loading && <Spinner animation="border" size="sm" />}
      </div>

      <Table striped bordered hover responsive className="data-table mb-4">
        <thead>
          <tr>
            <th>月</th>
            <th>予約数</th>
            <th>売上</th>
            <th>サイト手数料</th>
            <th>清掃費</th>
            <th>備品費</th>
            <th>固定費</th>
            <th>利益</th>
            <th>お支払額（清掃費＋折半）</th>
            <th>支払状況</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => {
            const paid = m.payment?.status === 'paid';
            const active = m.active; // 稼働月判定はバックエンドと共通のフラグを使う
            return (
              <tr
                key={m.month}
                onClick={() => setSelectedMonth(m.month)}
                style={{ cursor: 'pointer' }}
                className={selectedMonth === m.month ? 'table-primary' : (active ? '' : 'text-muted')}
              >
                <td style={{ whiteSpace: 'nowrap' }}>{m.month}</td>
                <td>{m.booking_count}</td>
                <td>{formatCurrency(m.gross)}</td>
                <td className={active ? 'text-danger' : ''}>{formatCurrency(-m.commission_total)}</td>
                <td className={active ? 'text-danger' : ''}>{formatCurrency(-m.cleaning_total)}</td>
                <td className={active ? 'text-danger' : ''}>{formatCurrency(-m.expenses_total)}</td>
                <td className={active ? 'text-danger' : ''}>{formatCurrency(-m.fixed_total)}</td>
                <td className={m.profit < 0 ? 'text-danger fw-bold' : (active ? 'fw-bold' : '')}>{formatCurrency(m.profit)}</td>
                <td className={active ? 'fw-bold' : ''} title={active ? `清掃費 ${formatCurrency(m.cleaning_total)} ＋ 折半 ${formatCurrency(m.owner_share)}` : ''}>
                  {formatCurrency(m.owner_payment_total)}
                </td>
                <td>
                  {active && (
                    <Badge bg={paid ? 'success' : 'warning'}>
                      {paid ? `支払済 ${formatDate(m.payment.paid_date)}` : '未払い'}
                    </Badge>
                  )}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  {active && (
                    <div className="row-actions d-flex gap-1 flex-wrap">
                      <Button
                        variant={paid ? 'outline-secondary' : 'outline-success'}
                        size="sm"
                        onClick={() => handleTogglePaid(m.month, m.payment, m.owner_payment_total)}
                      >
                        {paid ? '未払いに戻す' : '支払済みにする'}
                      </Button>
                      <Button variant="outline-primary" size="sm" onClick={() => handleExportStatement(m.month, 'xlsx')}>
                        明細書Excel
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleExportStatement(m.month, 'pdf')}>
                        明細書PDF
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        {totals && months.length > 0 && (
          <tfoot>
            <tr className="fw-bold">
              <td>{year}年 合計</td>
              <td>{totals.booking_count}</td>
              <td>{formatCurrency(totals.gross)}</td>
              <td className="text-danger">{formatCurrency(-totals.commission_total)}</td>
              <td className="text-danger">{formatCurrency(-totals.cleaning_total)}</td>
              <td className="text-danger">{formatCurrency(-totals.expenses_total)}</td>
              <td className="text-danger">{formatCurrency(-totals.fixed_total)}</td>
              <td>{formatCurrency(totals.profit)}</td>
              <td>{formatCurrency(totals.owner_payment_total)}</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        )}
      </Table>

      {/* 月次詳細 */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <h4 className="mb-0">月次詳細</h4>
        <Form.Control
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{ maxWidth: '180px' }}
        />
        <Button variant="outline-success" size="sm" onClick={openCreateExpense}>備品費を登録</Button>
        {monthDetail && (
          <>
            <Button variant="outline-primary" size="sm" onClick={() => handleExportStatement(selectedMonth, 'xlsx')}>
              支払明細書（Excel）
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => handleExportStatement(selectedMonth, 'pdf')}>
              支払明細書（PDF）
            </Button>
          </>
        )}
        {detailLoading && <Spinner animation="border" size="sm" />}
      </div>

      {monthDetail && (
        <>
          <Row className="mb-3 g-2">
            {[
              ['売上', monthDetail.gross, ''],
              ['サイト手数料', -monthDetail.commission_total, 'text-danger'],
              ['清掃費', -monthDetail.cleaning_total, 'text-danger'],
              ['備品費・固定費', -(monthDetail.expenses_total + monthDetail.fixed_total), 'text-danger'],
              [`利益折半（${monthDetail.owner_split_percent}%）`, monthDetail.owner_share, monthDetail.profit < 0 ? 'text-danger' : 'text-success'],
              ['お支払合計（清掃費＋折半）', monthDetail.owner_payment_total, 'text-primary'],
            ].map(([label, value, cls]) => (
              <Col key={label} xs={6} md={4} lg={2}>
                <Card className="h-100">
                  <Card.Body className="py-2 px-3">
                    <div className="text-muted small">{label}</div>
                    <div className={`fw-bold ${cls}`}>{formatCurrency(value)}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <h6>予約別内訳（{monthDetail.booking_count}件）</h6>
          <Table striped bordered hover responsive size="sm" className="data-table mb-4">
            <thead>
              <tr>
                <th>チェックイン</th>
                <th>ゲスト</th>
                <th>予約サイト</th>
                <th>宿泊料金</th>
                <th>手数料率</th>
                <th>サイト手数料</th>
                <th>清掃費</th>
                <th>差引額</th>
              </tr>
            </thead>
            <tbody>
              {monthDetail.bookings.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-muted">この月の予約はありません</td></tr>
              ) : (
                monthDetail.bookings.map((b, i) => (
                  <tr key={`${b.booking_id}-${i}`}>
                    <td>{formatDate(b.check_in_date)}</td>
                    <td>{b.guest_name}</td>
                    <td>{b.booking_source}</td>
                    <td>{formatCurrency(b.total_price)}</td>
                    <td>{b.commission_rate}%</td>
                    <td className="text-danger">{formatCurrency(-b.commission)}</td>
                    <td className="text-danger">{formatCurrency(-b.cleaning_fee)}</td>
                    <td className="fw-bold">{formatCurrency(b.net)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <h6>
            備品費（{monthDetail.expenses.length}件 / 分配対象 {formatCurrency(monthDetail.expenses_total)}
            {monthDetail.exempt_expenses_total > 0 && ` ＋ 対象外 ${formatCurrency(monthDetail.exempt_expenses_total)}`}）
          </h6>
          <Table striped bordered hover responsive size="sm" className="data-table mb-4" style={{ maxWidth: '800px' }}>
            <thead>
              <tr>
                <th>日付</th>
                <th>品名</th>
                <th>勘定科目</th>
                <th>金額</th>
                <th>備考</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {monthDetail.expenses.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-muted">この月の備品費はありません</td></tr>
              ) : (
                monthDetail.expenses.map((e) => (
                  <tr key={e.id} className={e.split_exempt ? 'text-muted' : ''}>
                    <td>{formatDate(e.date)}</td>
                    <td>
                      {e.label}
                      {e.split_exempt && <Badge bg="secondary" className="ms-1">分配対象外</Badge>}
                    </td>
                    <td>{e.category || '消耗品費'}</td>
                    <td>{formatCurrency(e.amount)}</td>
                    <td>{e.notes || '-'}</td>
                    <td>
                      <div className="row-actions d-flex gap-1">
                        <Button variant="outline-primary" size="sm" onClick={() => openEditExpense(e)}>編集</Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteExpense(e.id)}>削除</Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
            <h6 className="mb-0">
              月額固定費（分配対象 {formatCurrency(monthDetail.fixed_total)}
              {monthDetail.exempt_fixed_total > 0 && ` ＋ 対象外 ${formatCurrency(monthDetail.exempt_fixed_total)}`}）
            </h6>
            {monthDetail.fixed_override && <Badge bg="info">この月だけ上書き中</Badge>}
            <Button variant="outline-secondary" size="sm" onClick={openFixedModal}>
              この月だけ変更
            </Button>
          </div>
          <Table striped bordered hover responsive size="sm" className="data-table mb-4" style={{ maxWidth: '600px' }}>
            <thead>
              <tr>
                <th>項目</th>
                <th>勘定科目</th>
                <th>金額（円/月）</th>
              </tr>
            </thead>
            <tbody>
              {monthDetail.fixed_costs.length === 0 ? (
                <tr><td colSpan={3} className="text-center text-muted">稼働のない月は固定費を計上しません</td></tr>
              ) : (
                monthDetail.fixed_costs.map((c, i) => (
                  <tr key={`${c.label}-${i}`} className={c.split_exempt ? 'text-muted' : ''}>
                    <td>
                      {c.label}
                      {c.split_exempt && <Badge bg="secondary" className="ms-1">分配対象外</Badge>}
                    </td>
                    <td>{c.category || '通信費'}</td>
                    <td>{formatCurrency(c.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </>
      )}

      {/* 備品費モーダル */}
      <Modal show={showExpenseModal} onHide={() => setShowExpenseModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingExpenseId ? '備品費を編集' : '備品費を登録'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>日付 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm((p) => ({ ...p, date: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>金額（円） <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={7}>
                <Form.Group className="mb-3">
                  <Form.Label>品名 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={expenseForm.label}
                    onChange={(e) => setExpenseForm((p) => ({ ...p, label: e.target.value }))}
                    placeholder="例: シャンプー・トイレットペーパー補充"
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>勘定科目</Form.Label>
                  <Form.Select
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm((p) => ({ ...p, category: e.target.value }))}
                  >
                    {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    {expenseForm.category && !EXPENSE_CATEGORIES.includes(expenseForm.category) && (
                      <option value={expenseForm.category}>{expenseForm.category}</option>
                    )}
                  </Form.Select>
                  <Form.Text muted>決算資料の科目別集計に使われます</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="expense-split-exempt"
                label="オーナー分配の対象外（運営負担）"
                checked={expenseForm.split_exempt}
                onChange={(e) => setExpenseForm((p) => ({ ...p, split_exempt: e.target.checked }))}
              />
              <Form.Text muted>
                チェックすると利益折半の計算から除外され、オーナー向けの支払明細書にも載りません（決算資料には経費として計上）
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-1">
              <Form.Label>備考</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExpenseModal(false)}>キャンセル</Button>
          <Button
            variant="primary"
            onClick={handleSaveExpense}
            disabled={saving || !expenseForm.date || !expenseForm.label.trim() || !expenseForm.amount}
          >
            {saving ? '保存中...' : '保存'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 月別固定費モーダル */}
      <Modal show={showFixedModal} onHide={() => setShowFixedModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{fixedForm?.month} の固定費を変更</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fixedForm && (
            <>
              <p className="text-muted small">
                この月だけの上書きです。精算設定の基本固定費は変わりません。
                {fixedForm.is_override
                  ? '（現在この月は上書き済み。「基本設定に戻す」で解除できます）'
                  : '（現在は基本設定の値を表示しています）'}
              </p>
              {fixedForm.costs.map((c, i) => (
                <Row key={i} className="mb-2 g-2 align-items-center">
                  <Col xs={3}>
                    <Form.Control
                      type="text"
                      value={c.label}
                      placeholder="項目名"
                      onChange={(e) => setFixedForm((p) => {
                        const costs = [...p.costs];
                        costs[i] = { ...costs[i], label: e.target.value };
                        return { ...p, costs };
                      })}
                    />
                  </Col>
                  <Col xs={2}>
                    <Form.Control
                      type="number"
                      min="0"
                      value={c.amount}
                      placeholder="金額(円/月)"
                      onChange={(e) => setFixedForm((p) => {
                        const costs = [...p.costs];
                        costs[i] = { ...costs[i], amount: e.target.value };
                        return { ...p, costs };
                      })}
                    />
                  </Col>
                  <Col xs={3}>
                    <Form.Select
                      value={c.category || '通信費'}
                      title="決算集計用の勘定科目"
                      onChange={(e) => setFixedForm((p) => {
                        const costs = [...p.costs];
                        costs[i] = { ...costs[i], category: e.target.value };
                        return { ...p, costs };
                      })}
                    >
                      {EXPENSE_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                      {c.category && !EXPENSE_CATEGORIES.includes(c.category) && (
                        <option value={c.category}>{c.category}</option>
                      )}
                    </Form.Select>
                  </Col>
                  <Col xs={2}>
                    <Form.Check
                      type="checkbox"
                      id={`fixed-exempt-${i}`}
                      label="分配対象外"
                      title="チェックすると利益折半の計算から除外（運営負担・明細書にも非掲載）"
                      checked={!!c.split_exempt}
                      onChange={(e) => setFixedForm((p) => {
                        const costs = [...p.costs];
                        costs[i] = { ...costs[i], split_exempt: e.target.checked };
                        return { ...p, costs };
                      })}
                    />
                  </Col>
                  <Col xs={2}>
                    <Button
                      variant="outline-danger"
                      onClick={() => setFixedForm((p) => ({
                        ...p,
                        costs: p.costs.filter((_, j) => j !== i),
                      }))}
                    >
                      削除
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setFixedForm((p) => ({
                  ...p,
                  costs: [...p.costs, { label: '', amount: '', category: '通信費', split_exempt: false }],
                }))}
              >
                ＋ 項目を追加
              </Button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {fixedForm?.is_override && (
            <Button variant="outline-warning" onClick={handleResetFixed} disabled={saving} className="me-auto">
              基本設定に戻す
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowFixedModal(false)}>キャンセル</Button>
          <Button variant="primary" onClick={handleSaveFixed} disabled={saving}>
            {saving ? '保存中...' : 'この月に適用'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 精算設定モーダル */}
      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>精算設定</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {configForm && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>清掃費（円 / 予約）</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={configForm.cleaning_fee}
                      onChange={(e) => setConfigForm((p) => ({ ...p, cleaning_fee: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>オーナー折半率（%）</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      value={configForm.owner_split_percent}
                      onChange={(e) => setConfigForm((p) => ({ ...p, owner_split_percent: e.target.value }))}
                    />
                    <Form.Text muted>利益のうちオーナーへ支払う割合</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Label className="fw-bold">月額固定費（インターネット・サーバー等）</Form.Label>
              {configForm.monthly_costs.map((c, i) => (
                <Row key={i} className="mb-2 g-2 align-items-center">
                  <Col xs={3}>
                    <Form.Control
                      type="text"
                      value={c.label}
                      placeholder="項目名"
                      onChange={(e) => setConfigForm((p) => {
                        const monthly_costs = [...p.monthly_costs];
                        monthly_costs[i] = { ...monthly_costs[i], label: e.target.value };
                        return { ...p, monthly_costs };
                      })}
                    />
                  </Col>
                  <Col xs={2}>
                    <Form.Control
                      type="number"
                      min="0"
                      value={c.amount}
                      placeholder="金額(円/月)"
                      onChange={(e) => setConfigForm((p) => {
                        const monthly_costs = [...p.monthly_costs];
                        monthly_costs[i] = { ...monthly_costs[i], amount: e.target.value };
                        return { ...p, monthly_costs };
                      })}
                    />
                  </Col>
                  <Col xs={3}>
                    <Form.Select
                      value={c.category || '通信費'}
                      title="決算集計用の勘定科目"
                      onChange={(e) => setConfigForm((p) => {
                        const monthly_costs = [...p.monthly_costs];
                        monthly_costs[i] = { ...monthly_costs[i], category: e.target.value };
                        return { ...p, monthly_costs };
                      })}
                    >
                      {EXPENSE_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                      {c.category && !EXPENSE_CATEGORIES.includes(c.category) && (
                        <option value={c.category}>{c.category}</option>
                      )}
                    </Form.Select>
                  </Col>
                  <Col xs={2}>
                    <Form.Check
                      type="checkbox"
                      id={`config-exempt-${i}`}
                      label="分配対象外"
                      title="チェックすると利益折半の計算から除外（運営負担・明細書にも非掲載）"
                      checked={!!c.split_exempt}
                      onChange={(e) => setConfigForm((p) => {
                        const monthly_costs = [...p.monthly_costs];
                        monthly_costs[i] = { ...monthly_costs[i], split_exempt: e.target.checked };
                        return { ...p, monthly_costs };
                      })}
                    />
                  </Col>
                  <Col xs={2}>
                    <Button
                      variant="outline-danger"
                      onClick={() => setConfigForm((p) => ({
                        ...p,
                        monthly_costs: p.monthly_costs.filter((_, j) => j !== i),
                      }))}
                    >
                      削除
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                className="mb-3"
                onClick={() => setConfigForm((p) => ({
                  ...p,
                  monthly_costs: [...p.monthly_costs, { label: '', amount: '', category: '通信費', split_exempt: false }],
                }))}
              >
                ＋ 固定費を追加
              </Button>

              <div>
                <Form.Label className="fw-bold">予約サイト別手数料率（%）</Form.Label>
                {configForm.ota_rates.map((r, i) => (
                  <Row key={i} className="mb-2 g-2">
                    <Col xs={6}>
                      <Form.Control
                        type="text"
                        value={r.source}
                        placeholder="サイト名"
                        onChange={(e) => setConfigForm((p) => {
                          const ota_rates = [...p.ota_rates];
                          ota_rates[i] = { ...ota_rates[i], source: e.target.value };
                          return { ...p, ota_rates };
                        })}
                      />
                    </Col>
                    <Col xs={4}>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={r.rate}
                        placeholder="率(%)"
                        onChange={(e) => setConfigForm((p) => {
                          const ota_rates = [...p.ota_rates];
                          ota_rates[i] = { ...ota_rates[i], rate: e.target.value };
                          return { ...p, ota_rates };
                        })}
                      />
                    </Col>
                    <Col xs={2}>
                      <Button
                        variant="outline-danger"
                        onClick={() => setConfigForm((p) => ({
                          ...p,
                          ota_rates: p.ota_rates.filter((_, j) => j !== i),
                        }))}
                      >
                        削除
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setConfigForm((p) => ({
                    ...p,
                    ota_rates: [...p.ota_rates, { source: '', rate: '' }],
                  }))}
                >
                  ＋ サイトを追加
                </Button>
                <Form.Text as="div" muted className="mt-2">
                  予約管理の「予約媒体」名と一致させてください。登録がないサイトは「その他」の率が使われます。
                </Form.Text>
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfigModal(false)}>キャンセル</Button>
          <Button variant="primary" onClick={handleSaveConfig} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Ledger;
